/**
 * Le sous-barrel de l'espace ÉLÈVE — lu par les seuls ports élève (`ports/primary/…/student/`).
 *
 * Les écrans de l'élève ne passent pas par le barrel public (`./index.ts`) : l'app entière lit
 * celui-là, et l'espace élève n'y entre qu'allumé pour tous. D'ici là, ce fichier est la seule porte
 * vers ses pages, ses gabarits, ses organismes et les pièces de sa coquille ; ce que le parent lit
 * aussi (`<ParentHeader>`, `<BottomTab>`, `<ProfileSwitcherSheet>`, `<ActivitiesPage>`…) reste dans
 * le barrel public, et se lit de là.
 *
 * Les types `UI*` encore déclarés dans un composant ne sont pas réexportés ici : un lot qui en a
 * besoin les remonte dans `ports/primary/ui/types` (liste dans `ports/TRANSITION.md`).
 *
 * N'y entre que ce qu'un écran des ports lit : Metro évalue tout ce qu'un barrel réexporte. Les pages
 * de l'activité, de la correction, des félicitations et du live y sont entrées avec le socle de leur
 * câblage, avec les calques que leurs écrans composent (le chat, les feuilles de Sam, l'aide, la
 * liste des activités, l'absence du tuteur) ; un lot qui n'en lit finalement pas un le retire.
 */

// ── La coquille ──
export { StudentCounters } from "./molecules/StudentCounters"
export type { StudentCountersProps } from "./molecules/StudentCounters"
export type { AppearanceChoice } from "./molecules/ProfileSwitcherSheet/AppearanceSection"
export { PaymentBlockedDialog } from "./templates/PaymentBlockedDialog"
export type { PaymentBlockedDialogProps } from "./templates/PaymentBlockedDialog"

// ── Accueil ──
export { StudentHomePage } from "./pages/StudentHomePage"
export type { StudentHomePageProps } from "./pages/StudentHomePage"
export { NextSchoolAssessmentCard } from "./organisms/NextSchoolAssessmentCard"
export type { NextSchoolAssessmentCardProps } from "./organisms/NextSchoolAssessmentCard"
export { LiveAccessDialog } from "./templates/LiveAccessDialog"
export type { LiveAccessDialogProps, LiveAccessNotice } from "./templates/LiveAccessDialog"

// ── Activités ──
export { TrialOfferCard } from "./molecules/TrialOfferCard"
export type { TrialOfferCardProps } from "./molecules/TrialOfferCard"

// ── Mon tutorat et la messagerie ──
export { StudentCoachPage, StudentCoachTwoPane } from "./pages/StudentCoachPage"
export type { StudentCoachPageProps, StudentCoachTwoPaneProps } from "./pages/StudentCoachPage"
export { StudentTutorConversationPage } from "./pages/StudentTutorConversationPage"
export type { StudentTutorConversationPageProps } from "./pages/StudentTutorConversationPage"
export { TWO_PANE_FROM } from "./templates/TwoPaneTemplate"
export type { ComposerAttachSource } from "./molecules/MessageComposer"

// ── Contrôles ──
export { StudentAssessmentsPage } from "./pages/StudentAssessmentsPage"
export type { StudentAssessmentsPageProps, StudentAssessmentFormProps } from "./pages/StudentAssessmentsPage"

// ── Jeu ──
export { StudentQuestsPage } from "./pages/StudentQuestsPage"
export type { StudentQuestsPageProps } from "./pages/StudentQuestsPage"
export { StudentQuestDetailPage } from "./pages/StudentQuestDetailPage"
export type { StudentQuestDetailPageProps, StudentQuestDetailComposition } from "./pages/StudentQuestDetailPage"
export { StudentRankingPage } from "./pages/StudentRankingPage"
export type { StudentRankingPageProps } from "./pages/StudentRankingPage"
export { StudentShopPage } from "./pages/StudentShopPage"
export type { StudentShopPageProps, StudentShopNotice, StudentShopSheetProps } from "./pages/StudentShopPage"

// ── Compte et paramètres ──
export { StudentAccountPage } from "./pages/StudentAccountPage"
export type { StudentAccountNotice, StudentAccountPageProps } from "./pages/StudentAccountPage"
export { StudentSettingsPage } from "./pages/StudentSettingsPage"
export type { StudentSettingsPageProps } from "./pages/StudentSettingsPage"
export { StudentSettingsGeneralPage } from "./pages/StudentSettingsGeneralPage"
export type { StudentGeneralSettingsField, StudentSettingsGeneralPageProps } from "./pages/StudentSettingsGeneralPage"
export { StudentSettingsProfilePage } from "./pages/StudentSettingsProfilePage"
export type { StudentProfileSettingsField, StudentSettingsProfilePageProps } from "./pages/StudentSettingsProfilePage"
export { StudentSettingsAddressPage } from "./pages/StudentSettingsAddressPage"
export type { StudentAddressSettingsField, StudentSettingsAddressPageProps } from "./pages/StudentSettingsAddressPage"
export { StudentSettingsPreferencesPage } from "./pages/StudentSettingsPreferencesPage"
export type { StudentSettingsPreferencesPageProps } from "./pages/StudentSettingsPreferencesPage"
export { StudentPasswordPage } from "./pages/StudentPasswordPage"
export type { PasswordChangeField, StudentPasswordPageProps } from "./pages/StudentPasswordPage"
export { StudentCloseAccountPage } from "./pages/StudentCloseAccountPage"
export type { StudentCloseAccountPageProps } from "./pages/StudentCloseAccountPage"
export { StudentValidateEmailPage } from "./pages/StudentValidateEmailPage"
export type { StudentValidateEmailNotice } from "./pages/StudentValidateEmailPage"
export { StudentProfilePromptModal } from "./templates/StudentProfilePromptModal"
export type { StudentProfilePromptModalProps } from "./templates/StudentProfilePromptModal"

// ── L'exécution d'une activité ──
export { StudentActivityPage } from "./pages/StudentActivityPage"
export type { StudentActivityCopy, StudentActivityPageProps } from "./pages/StudentActivityPage"
export { StudentCorrectionPage } from "./pages/StudentCorrectionPage"
export type { StudentCorrectionPageProps, StudentCorrectionStep } from "./pages/StudentCorrectionPage"
export { StudentActivityCongratsPage } from "./pages/StudentActivityCongratsPage"
export type { ActivityCongratsNext, StudentActivityCongratsPageProps } from "./pages/StudentActivityCongratsPage"
export { ActivityStartCountdown } from "./molecules/ActivityStartCountdown"
export type { ActivityStartCountdownProps, StartCountdownKind } from "./molecules/ActivityStartCountdown"
export { EnonceReadingSheet } from "./templates/EnonceReadingSheet"
export type { EnonceReadingSheetProps } from "./templates/EnonceReadingSheet"
export { ActivityCopyCamera } from "./organisms/ActivityCopyCamera"
export type {
    ActivityCopyCameraFailure,
    ActivityCopyCameraPhase,
    ActivityCopyCameraProps,
} from "./organisms/ActivityCopyCamera"
export { SamFloatingTile } from "./organisms/SamFloatingTile"
export { SamSheet } from "./organisms/SamSheet"
export type { SamSheetProps } from "./organisms/SamSheet"
export { SamConversation } from "./organisms/SamConversation"
export type { SamConversationProps } from "./organisms/SamConversation"

// ── Le live ──
export { StudentLiveEntryPage } from "./pages/StudentLiveEntryPage"
export type { StudentLiveEntryPageProps } from "./pages/StudentLiveEntryPage"
export { StudentStartingBlockPage } from "./pages/StudentStartingBlockPage"
export type { StudentStartingBlockPageProps } from "./pages/StudentStartingBlockPage"
export { StudentLivePage } from "./pages/StudentLivePage"
export type { StudentLivePageProps, UILiveScreenNotice } from "./pages/StudentLivePage"
export { StudentLiveEndPage } from "./pages/StudentLiveEndPage"
export type { StudentLiveEndPageProps } from "./pages/StudentLiveEndPage"
export { StudentLiveQuizPage } from "./pages/StudentLiveQuizPage"
export type {
    StudentLiveQuizExit,
    StudentLiveQuizPageProps,
    StudentLiveQuizStep,
    UILiveQuizQuestion,
} from "./pages/StudentLiveQuizPage"
export { LiveChat } from "./organisms/LiveChat"
export type { LiveChatProps } from "./organisms/LiveChat"
export { CheckpointConversation } from "./organisms/CheckpointConversation"
export type { CheckpointConversationProps } from "./organisms/CheckpointConversation"
export { LiveHelpSheet } from "./templates/LiveHelpSheet"
export type { LiveHelpSheetProps } from "./templates/LiveHelpSheet"
export { LiveActivitiesSheet } from "./templates/LiveActivitiesSheet"
export type { LiveActivitiesSheetProps } from "./templates/LiveActivitiesSheet"
export { VoiceRecorderSheet, RecorderClock } from "./templates/VoiceRecorderSheet"
export { DeviceCheckPanel } from "./organisms/DeviceCheckPanel"
export { DeviceCheckStep } from "./organisms/DeviceCheckStep"
export { CameraPreviewFrame } from "./molecules/CameraPreviewFrame"
export { DeviceCheckSoundTest } from "./molecules/DeviceCheckSoundTest"
export { DeviceCheckGuidance } from "./molecules/DeviceCheckGuidance"
export { PermissionPromptCoach } from "./molecules/PermissionPromptCoach"
export { LiveBrowserChoice } from "./organisms/LiveBrowserChoice"
export { CorrectionChallenge } from "./organisms/CorrectionChallenge"
export { CorrectionPause } from "./organisms/CorrectionPause"
export { QuizReview } from "./organisms/QuizReview"
export { LiveRatingModal } from "./templates/LiveRatingModal"
export type { SamSheetDetent } from "./organisms/SamSheet"
export { LiveTutorAbsentDialog } from "./templates/LiveTutorAbsentDialog"
export type { LiveTutorAbsentDialogProps } from "./templates/LiveTutorAbsentDialog"
export { LiveClosedDialog } from "./templates/LiveClosedDialog"
export type { LiveClosedDialogProps } from "./templates/LiveClosedDialog"
export { STUDENT_SESSION_ACTIONS_WITH_JOIN } from "./templates/LiveSessionActionsSheet"
export { LiveAppBanner } from "./molecules/LiveAppBanner"
export { LiveAutoModeSheet } from "./templates/LiveAutoModeSheet"
export type { LiveAutoModeSheetProps } from "./templates/LiveAutoModeSheet"
export { LiveDevicesSheet } from "./templates/LiveDevicesSheet"
export { LiveTandemBubble } from "./organisms/LiveTandemBubble"
export type { LiveTandemBubbleProps, UITandemLead } from "./organisms/LiveTandemBubble"
